import {
  hasFullAppAccess,
  type AppToBuildStr,
  type NewPurchaseState,
} from "@petergok/blue-collar-shared";

export type AutoRedeemErrorReason =
  | "not_found"
  | "already_redeemed"
  | "already_has_access"
  | "wrong_exam_type"
  | "invalid_input"
  | "rate_limited"
  // We gave up waiting. Distinct from "unknown" because the redemption may well have
  // succeeded server-side — the copy must not claim it failed.
  | "timed_out"
  | "unknown";

const AUTO_REDEMPTION_RETRY_DELAYS_MS = [1_000, 4_000] as const;

/**
 * Checks only stored provider/server access. Referral trials are merged at read
 * time for feature gates, but must not prevent redeeming real organization access.
 */
export function hasFullStoredAccessForOrgRedemption(
  appToBuild: AppToBuildStr,
  purchaseState: NewPurchaseState | undefined,
): boolean {
  return hasFullAppAccess(appToBuild, purchaseState?.purchaseAccess);
}

export function getAutoRedemptionRetryDelay(attempt: number): number | null {
  return AUTO_REDEMPTION_RETRY_DELAYS_MS[attempt] ?? null;
}

export function isAmbiguousRedemptionRecoveryCandidate(
  reason: AutoRedeemErrorReason,
  hadAmbiguousFailure: boolean,
) {
  return Boolean(
    hadAmbiguousFailure &&
    (reason === "already_redeemed" || reason === "already_has_access"),
  );
}

export function hasBlockingRedemptionPresentation({
  isMobileWeb,
  registeredBlockerCount,
  showStudyCoachmark,
  signedOutModalOpen,
}: {
  isMobileWeb: boolean;
  registeredBlockerCount: number;
  showStudyCoachmark: boolean;
  signedOutModalOpen: boolean;
}) {
  return Boolean(
    registeredBlockerCount > 0 ||
    (!isMobileWeb && showStudyCoachmark) ||
    signedOutModalOpen,
  );
}

const DEFERRED_ERROR_PATHS = new Set([
  "/create-profile",
  "/create-profile-standalone",
]);

const DEFERRED_STATUS_PATHS = new Set([
  "/",
  "/redeem",
  "/exam-selection-standalone",
]);

interface RedemptionStatusDeferralOpts {
  pathname: string;
  blockingPresentationActive: boolean;
  storeReady: boolean;
  stateSelectionPending: boolean;
}

interface RedemptionErrorDeferralOpts extends RedemptionStatusDeferralOpts {
  profileCompletionPending: boolean;
}

function isAuthPath(pathname: string) {
  return (
    pathname === "/sign-in" ||
    pathname.startsWith("/sign-in/") ||
    pathname === "/sign-up" ||
    pathname.startsWith("/sign-up/")
  );
}

export function shouldDeferRedemptionStatus({
  pathname,
  blockingPresentationActive,
  storeReady,
  stateSelectionPending,
}: RedemptionStatusDeferralOpts) {
  return Boolean(
    !storeReady ||
    blockingPresentationActive ||
    stateSelectionPending ||
    DEFERRED_STATUS_PATHS.has(pathname) ||
    isAuthPath(pathname),
  );
}

export function shouldDeferRedemptionError({
  pathname,
  blockingPresentationActive,
  storeReady,
  profileCompletionPending,
  stateSelectionPending,
}: RedemptionErrorDeferralOpts) {
  return (
    profileCompletionPending ||
    DEFERRED_ERROR_PATHS.has(pathname) ||
    shouldDeferRedemptionStatus({
      pathname,
      blockingPresentationActive,
      storeReady,
      stateSelectionPending,
    })
  );
}

export function shouldDeferRedemptionAttempt(
  opts: RedemptionErrorDeferralOpts,
) {
  // Starting the request under onboarding can consume the code and trigger a
  // route change before the user finishes the active workflow. Keep the code
  // pending until the same point at which any outcome is safe to present.
  return shouldDeferRedemptionError(opts);
}
